import type { useTranslations } from 'next-intl';
import type { LotteryName } from '../../../../../lottery-purchases/types';
import { useMapPrizeTablesDefault, useMapPrizeTablesMegaMillions, useMapPrizeTablesPowerPlay, useMapPrizeTablesStaticPrize } from '../../../../hooks';
import type { AdditionalDrawingInfo, PrizeTableTooltips, TableRow } from '../../../../types';
import { PrizeTablesList, PrizeTablesWithMultiplier, PrizeTablesWithSwitchButtons } from '../../components';

type DataMapper = typeof useMapPrizeTablesDefault | typeof useMapPrizeTablesPowerPlay | typeof useMapPrizeTablesStaticPrize | typeof useMapPrizeTablesMegaMillions;

type PrizeTableStrategy = {
  matcher: (params: { lotteryName: LotteryName; additionalDrawingInfo?: AdditionalDrawingInfo }) => boolean;
  Component: React.FC;
  dataMapper: DataMapper;
  multipliersConfig?: [multipliers: number[], defaultMultiplier: number];
  tooltips?: (t: ReturnType<typeof useTranslations>) => PrizeTableTooltips;
};

type TooltipConfig = {
  rowIndex: number;
  tooltips: Array<{
    field: keyof TableRow;
    translationKey: string;
  }>;
};

/**
 * Creates a PrizeTableTooltips map.
 * @param configs - Array of tooltip configurations for specific rows
 * @param t - Translation function from useTranslations
 * @returns Map of row indices to field tooltips
 *
 * @example
 * createTooltips([
 *   {
 *     rowIndex: 0,
 *     tooltips: [
 *       { field: 'amount', translationKey: 'prize_table_amount_info' },
 *       { field: 'winners', translationKey: 'prize_table_winners_info' }
 *     ]
 *   }
 * ], t)
 */
function createTooltips(configs: TooltipConfig[], t: ReturnType<typeof useTranslations>): PrizeTableTooltips {
  const tooltipsMap: PrizeTableTooltips = new Map();
  configs.forEach(({ rowIndex, tooltips }) => {
    const fieldTooltipsMap = new Map<keyof TableRow, string>();
    tooltips.forEach(({ field, translationKey }) => {
      fieldTooltipsMap.set(field, t(translationKey));
    });
    tooltipsMap.set(rowIndex, fieldTooltipsMap);
  });
  return tooltipsMap;
}

/**
 * Main logic for prize table strategies. Based on lottery name and additional drawing info will render appropriate component and data mapper.
 *
 * IMPORTANT:
 * Strategies are matched top-down.
 * More specific matchers MUST come first.
 */
const prizeTableStrategies: PrizeTableStrategy[] = [
  {
    matcher: ({ lotteryName, additionalDrawingInfo }) => lotteryName === 'megaMillions' && typeof additionalDrawingInfo?.winnersByRankMultiplier !== 'object',
    Component: PrizeTablesWithSwitchButtons,
    dataMapper: useMapPrizeTablesPowerPlay,
  },
  {
    matcher: ({ lotteryName }) => lotteryName === 'megaMillions',
    Component: PrizeTablesWithMultiplier,
    dataMapper: useMapPrizeTablesMegaMillions,
    multipliersConfig: [[2, 3, 4, 5, 10], 3],
    tooltips: t =>
      createTooltips(
        [
          {
            rowIndex: 0,
            tooltips: [{ field: 'amount', translationKey: 'prize_table_megaMillions_amount_1' }],
          },
        ],
        t
      ),
  },
  {
    matcher: ({ lotteryName }) => lotteryName === 'powerBall',
    Component: PrizeTablesWithSwitchButtons,
    dataMapper: useMapPrizeTablesPowerPlay,
  },
  {
    matcher: ({ lotteryName }) => lotteryName === 'euroDreams' || lotteryName === 'euroMillionsGo' || lotteryName === 'luckyForLife',
    Component: PrizeTablesList,
    dataMapper: useMapPrizeTablesStaticPrize,
  },
];

/**
 * Default (fallback) strategy for prize table.
 * Matcher is always true.
 * @returns PrizeTablesList to be rendered together with useMapPrizeTablesDefault as data mapper.
 */
const defaultPrizeTableStrategy: PrizeTableStrategy = {
  matcher: () => true,
  Component: PrizeTablesList,
  dataMapper: useMapPrizeTablesDefault,
};

/**
 * Resolves the prize table strategy based on the lottery name and additional drawing info.
 * @param params - The parameters to match against.
 * @param lotteryName - The name of the lottery, main criteria to match against.
 * @param additionalDrawingInfo - (Optional) Additional drawing information, used for certain lottery types (to determine legacy megaMillion type).
 * @returns Custom component to be rendered together with the data mapper.
 */
function resolvePrizeTableStrategy(params: { lotteryName: LotteryName; additionalDrawingInfo?: AdditionalDrawingInfo }) {
  return prizeTableStrategies.find(strategy => strategy.matcher(params)) ?? defaultPrizeTableStrategy;
}

export { resolvePrizeTableStrategy };
export type { DataMapper, PrizeTableStrategy };
